import type {ByocCollectionWithLocation} from '@src/types/dataCollections.ts';
import {
  ByocLayerSubtype,
  CollectionOwner,
  DataCollectionFilter,
  PLANET_BUCKETS,
  PLANET_SANDBOX_COLLECTION_IDS,
  STAC_COLLECTION_ID_PREFIX,
} from '@src/constants/dataCollections.ts';
import {
  DATA_COLLECTION_ROUTES,
  DataCollectionsRoutePattern,
} from '@src/constants/routes.ts';

const PROTOCOL_PREFIX = /^[a-z0-9]+:\/\//;

export function validateS3Bucket(bucket: string): {
  valid: boolean;
  message: string;
} {
  if (PROTOCOL_PREFIX.test(bucket)) {
    const sanitizedBucket = sanitizeS3UrlToBucket(bucket);
    const message =
      sanitizedBucket === bucket
        ? 'Please provide just the bucket name instead of URL.'
        : `Please provide just the bucket name, e.g., '${sanitizedBucket}'`;
    return {valid: false, message};
  }
  return {valid: true, message: ''};
}

const PROTOCOL_S3 = 's3://';
const PROTOCOL_HTTP_AMAZONAWS_COM =
  /^https?:\/\/([a-z0-9-.]*)\.s3[a-z0-9-]*\.amazonaws\.com$/;

function sanitizeS3UrlToBucket(bucket: string): string {
  if (bucket.startsWith(PROTOCOL_S3)) {
    return bucket.substring(PROTOCOL_S3.length);
  }
  if (PROTOCOL_HTTP_AMAZONAWS_COM.test(bucket)) {
    return bucket.replace(PROTOCOL_HTTP_AMAZONAWS_COM, '$1');
  }
  return bucket;
}

export const isTpdiCollection = (collection: ByocCollectionWithLocation) =>
  collection.additionalData?.isTpdiCollection === true &&
  !isPlanetCollection(collection);

export const isPlanetCollection = (collection?: ByocCollectionWithLocation) =>
  collection?.additionalData?.dataType?.provider === 'planet' ||
  (!!collection?.s3Bucket && PLANET_BUCKETS.includes(collection.s3Bucket));

export const isPlanetOrTpdiCollection = (
  collection: ByocCollectionWithLocation,
) => isTpdiCollection(collection) || isPlanetCollection(collection);

export const isPlanetTpdiCollection = (
  collection?: ByocCollectionWithLocation,
) =>
  collection?.additionalData?.dataType?.provider === 'planet' &&
  collection?.additionalData?.isTpdiCollection === true;

export function isPlanetSandboxCollection(id: string) {
  return PLANET_SANDBOX_COLLECTION_IDS.includes(id);
}

export const isUserCollection = (collection: ByocCollectionWithLocation) =>
  !(
    isPlanetOrTpdiCollection(collection) ||
    isPlanetSandboxCollection(collection.id)
  );

export const isSharedCollection = (
  collection?: ByocCollectionWithLocation,
): boolean => collection?.owner === CollectionOwner.SHARED;

export const getCollectionParentTab = (
  collection: ByocCollectionWithLocation,
) => {
  if (collection.owner === CollectionOwner.SANDBOX) {
    return DataCollectionFilter.sandboxData;
  } else if (isPlanetCollection(collection)) {
    return DataCollectionFilter.planet;
  } else if (isTpdiCollection(collection)) {
    return DataCollectionFilter.tpdi;
  }

  return DataCollectionFilter.byocZarr;
};

export const getCollectionParentLink = (
  collection: ByocCollectionWithLocation,
) => {
  const queryString = new URLSearchParams({
    tab: getCollectionParentTab(collection),
  }).toString();

  return `${DataCollectionsRoutePattern.root}?${queryString}`;
};

export const getStacCollectionId = (collection: ByocCollectionWithLocation) =>
  STAC_COLLECTION_ID_PREFIX[collection.type ?? ByocLayerSubtype.BYOC] +
  collection.id;

export const getCollectionHashRedirect = (hash: string): string | null => {
  if (!hash.startsWith('#')) {
    return null;
  } else if (!hash.startsWith('#/collection')) {
    // A general non-collection route, just strip off the hashslash
    // (and if there's nothing left after that, return #, because
    // otherwise react-router won't strip it out of the hash)
    return hash.replace(/^#\//, '') || '#';
  }

  // Check for /#/collection/ URLs (where we drop the "collection")
  const [collectionId, tab] = hash.replace(/^#\/collection\//, '').split('/');
  if (collectionId) {
    if (tab && ['arrays', 'configurations', 'tiles'].includes(tab)) {
      return DATA_COLLECTION_ROUTES.collection[
        tab as 'arrays' | 'configurations' | 'tiles'
      ](collectionId);
    } else {
      return DATA_COLLECTION_ROUTES.collection.detail(collectionId);
    }
  }

  return null;
};
